import { type ReactElement } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import { logger } from '~/lib/logger'
import { ArticleForm, articleQuery, articlesQuery, useUpdateArticle } from '~/features/help-kb'

/**
 * `/admin/help-kb/$articleId/edit` — edit one help article. A full page, not
 * a modal: see `ArticleForm`'s own header comment for why.
 */
export const Route = createFileRoute('/admin/help-kb/$articleId/edit')({
  component: EditArticlePage,
})

function EditArticlePage(): ReactElement {
  const { articleId } = Route.useParams()
  const navigate = useNavigate()

  const articleQueryResult = useQuery(articleQuery(articleId))
  // Only used for the module datalist's suggestions, same as the list page.
  const list = useQuery(articlesQuery())
  const updateMutation = useUpdateArticle()

  const modules = [...new Set((list.data?.articles ?? []).map((article) => article.module))].sort()

  const backTo = () => void navigate({ to: '/admin/help-kb' })

  if (articleQueryResult.isPending) {
    return (
      <div className="ka-page" aria-busy="true">
        <div className="ka-sk ka-sk--line-flex-2" />
      </div>
    )
  }

  if (articleQueryResult.error || !articleQueryResult.data) {
    return (
      <div className="ka-page">
        <div className="ka-card">
          <div className="ka-empty">
            <div className="ka-empty-title">Article not found</div>
            <Link to="/admin/help-kb" className="ka-btn ka-btn--secondary ka-btn--sm">
              <ArrowLeft size={14} aria-hidden="true" /> Back to Help Knowledge Base
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const article = articleQueryResult.data

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm" onClick={backTo}>
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </button>
          <h1 className="ka-page-header-title">
            Edit {article.title} <span className="ka-chip ka-chip--muted">v{article.version}</span>
          </h1>
        </div>
      </header>

      <ArticleForm
        article={article}
        modules={modules}
        onCancel={backTo}
        isSaving={updateMutation.isPending}
        error={updateMutation.error}
        onSave={(values) => {
          const { slug: _slug, ...patch } = values
          updateMutation.mutate({ id: article.id, patch }, {
            onSuccess: backTo,
            onError: (cause) => logger.warn('help-kb.update.failed', { cause }),
          })
        }}
      />
    </div>
  )
}
